import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToast } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useCreateUnsolicitedFeedback } from '../../../hooks/use-feedback-api';
import { useUpdateFeedbackApi } from '../../../hooks/use-update-feedback-api';
import { ReferenceEntity } from '../../../lib/entities/reference';
import { FeedbackEvent } from '../../../types/feedback-event';
import { QueryKey } from '../../../types/query-key';

interface Input {
	evaluationId?: string;
	feedbackText: string;
	onClose: VoidFunction;
	onReset: VoidFunction;
	recipientId: string;
	reference?: ReferenceEntity;
}

interface OnSubmitParams {
	isDraft: boolean;
}

export const useSubmitUnrequestedFeedback = ({ evaluationId, feedbackText, onClose, onReset, recipientId, reference }: Input) => {
	const { t } = useTranslation('feedback');
	const toast = useToast();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const { isLoading: isCreating, mutate: create } = useCreateUnsolicitedFeedback();
	const { isLoading: isUpdating, mutate: update } = useUpdateFeedbackApi();
	const isLoading = isCreating || isUpdating;
	const currentUser = useCurrentUser();

	const onSubmit = async ({ isDraft }: OnSubmitParams) => {
		const unrequestedFeedbackOptions = {
			onSuccess: () => {
				toast({
					position: 'bottom-right',
					title: isDraft ? t('feedback.unrequested.draft_success_message') : t('feedback.unrequested.success_message'),
					status: 'success',
				});
				dispatch(FeedbackEvent.UnsolicitedCreated);
				queryClient.invalidateQueries([QueryKey.UnrequestedFeedbackDrafts]);
				queryClient.invalidateQueries([QueryKey.ConsolidatedFeedbackIndex]);
				onReset();
				onClose();
			},
			onError: () => {
				toast({
					title: t('feedback.unrequested.failure_message'),
					status: 'error',
				});
			},
		};

		if (evaluationId) {
			update(
				{
					isDraft,
					receiverId: recipientId,
					reference,
					text: feedbackText,
					feedbackId: evaluationId,
				},
				unrequestedFeedbackOptions
			);
		} else {
			create(
				{
					giverId: currentUser.id,
					receiverId: recipientId,
					text: feedbackText,
					isDraft,
					reference,
				},
				unrequestedFeedbackOptions
			);
		}
	};

	return { onSubmit, isLoading };
};
